<template>
  <v-app>
    <v-main>
      <v-locale-provider rtl>
        <router-view/>
      </v-locale-provider>
    </v-main>
  </v-app>
</template>

<script setup>
  import { ref, computed, provide, onUnmounted, watch } from 'vue'
  import { onSnapshot, collection, doc, getDoc } from 'firebase/firestore'
  import { onAuthStateChanged } from 'firebase/auth'
  import { useRoute } from 'vue-router'
  import { db, auth } from '@/firebase'

  const route = useRoute()

  const legacyOwnerUid = 'EjrNn2sqQJgdoPsppRbu0719uaf1'
  const mappedLegacyUid = 'pZGq9JUlCdNC2NqfZuIl'

  const recepies = ref([])
  const loading = ref(true)
  const currentUser = ref(null)
  const userMetaData = ref(null)
  const selectedBookUserId = ref('')

  let closeRecipesSubscription = null
  let closeAuthSubscription = null

  const normalizeUserId = (uid) => {
    if (uid === legacyOwnerUid) {
      return mappedLegacyUid
    }
    return uid
  }

  const closeRecipes = () => {
    if (closeRecipesSubscription) {
      closeRecipesSubscription()
      closeRecipesSubscription = null
    }
  }

  const subscribeToRecipes = (uid) => {
    closeRecipes()
    if (!uid) {
      recepies.value = []
      loading.value = false
      return
    }

    loading.value = true
    const recepiesColl = collection(db, 'users', uid, 'recepies')
    closeRecipesSubscription = onSnapshot(
      recepiesColl,
      (snapshot) => {
        recepies.value = snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        loading.value = false
      },
      (error) => {
        console.log(error)
        recepies.value = []
        loading.value = false
      }
    )
  }

  const resolveSelectedBookUserId = () => {
    const fromParams = String(route.params.uid || '').trim()
    if (fromParams) {
      return normalizeUserId(fromParams)
    }

    if (currentUser.value?.uid) {
      return normalizeUserId(currentUser.value.uid)
    }
    return ''
  }

  const reload = () => {
    selectedBookUserId.value = resolveSelectedBookUserId()
    subscribeToRecipes(selectedBookUserId.value)
  }

  closeAuthSubscription = onAuthStateChanged(auth, async (user) => {
    currentUser.value = user
    if (!user) {
      userMetaData.value = null
      reload()
      return
    }

    const uid = normalizeUserId(user.uid)
    try {
      const userDoc = doc(db, 'users', uid)
      const docSnap = await getDoc(userDoc)
      userMetaData.value = docSnap.exists() ? docSnap.data() : null
    } catch (error) {
      console.log('Error getting user metadata:', error)
      userMetaData.value = null
    }
    reload()
  })

  watch(
    () => route.params.uid,
    () => {
      reload()
    }
  )

  const keywords = computed(() => {
    return [...new Set(recepies.value.map((x) => x.keywords || []).flat(1))].sort()
  })

  provide('loading', loading)
  provide('recepies', recepies)
  provide('keywords', keywords)
  provide('currentUser', currentUser)
  provide('reload', reload)
  provide('userMetaData', userMetaData)
  provide('selectedBookUserId', selectedBookUserId)

  onUnmounted(() => {
    closeRecipes()
    if (closeAuthSubscription) {
      closeAuthSubscription()
    }
  })
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Amatic+SC&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500&display=swap');

@font-face {
    font-family: miri;
    src: url('@/assets/miriwin-webfont.ttf');
}
</style>

